<template>
  <!-- eslint-disable max-len -->
  <div
    v-show="true"
    class="verify-info-read-desktop"
  >
    <!-- start/stop date updated due to email domain check -->
    <Banner
      v-if="(coa.medUpdated && coa.startDate) || (sdUpdated && coa.stopDate)"
      class="mt-6"
    >
      <template #heading>
        <!-- Start Update Updated Banner -->
        <p
          v-if="coa.medUpdated"
          ref="startUpdatedBanner"
        >
          Your mail forwarding start date has been updated to {{ parseAndFormatDate(coa.startDate) }}
        </p>
        <!-- Stop Update Updated Banner -->
        <p
          v-if="sdUpdated"
          ref="stopUpdatedBanner"
          class="mt-4"
        >
          Your mail forwarding end date has been updated to {{ parseAndFormatDate(coa.stopDate) }}
        </p>
      </template>
    </Banner>
    <div class="c-hairline mt-8" />
    <!-- Choice verifyInfo new design(wide view)---------------------------------------------------------------------------------------------------- -->

    <div
      v-if="showVerifyInfoWideView"
      class="choice__experience--services--side"
    >
      <div
        ref="infoRead"
        class="py-8"
      >
        <h3 class="font-bold">
          Move Information
        </h3>
        <!-- Display if the modalNewConstruction is opened and they hit "Yes" or "No" -->
        <template
          v-if="overrideAms"
        >
          <ErrorAlert
            v-if="$route.query.errorException === 'NewAddressFieldFullAddressException'"
            ref="newAddressFieldFullAddress"
            class="mt-2"
          >
            We cannot find the address in our records. The "Street Address" field should not include the city, state or ZIP. Please click the "Edit" button to remove the city, state or ZIP, then click "Save." If you are sure this is the correct address, you may proceed by clicking "Next."
          </ErrorAlert>
          <ErrorAlert
            v-if="$route.query.errorException === 'InvalidPrimaryInformationException'"
            ref="invalidPrimaryInformation"
            class="mt-2"
          >
            We cannot locate the entered street number in our records. Please click the "Edit" button to correct the street number of the street address, then click "Save." If you are certain the existing address is correct, you may proceed by clicking "Next."
          </ErrorAlert>
          <ErrorAlert
            v-if="$route.query.errorException === 'UnneededSecondaryInformationException'"
            ref="unneededSecondaryInformation"
            class="mt-2"
          >
            According to our records, there is no unit or apartment number associated with this address. Please verify the address for accuracy and click "Edit" then "Save" to make corrections.
          </ErrorAlert>
          <ErrorAlert
            v-if="$route.query.errorException === 'InvalidSecondaryInformationException'"
            ref="invalidSecondaryInformation"
            class="mt-2"
          >
            A unit or apartment number is either missing, incomplete or invalid. Please click "Edit" to add the correct number/designation after the street name in the "Street" field, then click "Save." USPS will standardize your address upon submission.
          </ErrorAlert>
          <ErrorAlert
            v-if="$route.query.errorException === 'AmsInvalidAddressException'"
            ref="invalid"
            class="mt-2"
          >
            We cannot find the address you're moving to in our records. Please verify the address for accuracy and click "Edit" then "Save" to make corrections.
          </ErrorAlert>
          <!-- default error when multiple businesses -->
          <ErrorAlert
            v-if="isMultipleAddressCoa"
            ref="invalid"
            class="mt-2"
          >
            We cannot find the address you're moving to in our records. Please verify the address for accuracy and click "Edit" then "Update" to make corrections. If you are certain the existing address is correct, you may proceed by clicking "Next."
          </ErrorAlert>
          <ErrorAlert
            ref="overrideMessage"
            class="mt-2"
          >
            Errors with your address may result in delays with mail forwarding.
          </ErrorAlert>
        </template>
        <div class="flex mt-6">
          <div class="w-1/2">
            <CoaItem
              v-if="coa.moverType === 'BUSINESS'"
              id="business-name"
              label="Business Name"
            >
              {{ coa.businessName }}

              <p :class="{'verify-info-alias-list' : businessAliases.length >= 5}">
                <span
                  v-for="alias in businessAliases"
                  :key="alias"
                >
                  <br>{{ alias }}
                </span>
              </p>
            </CoaItem>
            <CoaItem
              v-else
              label="Name"
            >
              {{ [mover.firstName, mover.middleName, mover.lastName ] | formatName }}
            </CoaItem>
            <CoaItem
              label="Who's Moving?"
              class="mt-6"
            >
              {{ coa.moverType }}
            </CoaItem>
            <CoaItem
              label="Email Address"
              class="mt-6"
            >
              {{ mover.email }}
            </CoaItem>
            <CoaItem
              label="Phone Number"
              class="mt-6"
            >
              {{ (mover.homePhone || mover.mobilePhone) | formatPhone }}
            </CoaItem>
            <CoaItem
              label="Phone Type"
              class="mt-6"
            >
              {{ mover.homePhone && 'Home' }}
              {{ mover.mobilePhone && 'Mobile' }}
            </CoaItem>
          </div>
          <div class="w-1/2">
            <CoaItem label="Type of Move">
              {{ coa.forwardType }}
            </CoaItem>
            <CoaItem
              v-if="coa.forwardType === 'PERMANENT'"
              label="Requested Mail Forwarding Date"
              class="mt-6"
            >
              <!-- Monday, September 14, 2020 -->
              {{ parseAndFormatDate(coa.startDate) }}
            </CoaItem>
            <template v-if="coa.forwardType === 'TEMPORARY'">
              <CoaItem
                label="Requested Start Forwarding Date"
                class="mt-6"
              >
                {{ parseAndFormatDate(coa.startDate) }}
              </CoaItem>
              <CoaItem
                label="Requested End Forwarding Date"
                class="mt-6"
              >
                {{ parseAndFormatDate(coa.stopDate) }}
              </CoaItem>
            </template>
            <CoaItem
              label="Old Address"
              class="mt-6"
            >
              {{ oldAddress.amsCompleteAddress }}<br>
              {{ oldAddress.amsCity }}, {{ oldAddress.amsState }} {{ formatZip9(oldAddress.amsZip9) }}<br>
              {{ oldAddress.amsUrbanizationName }}
            </CoaItem>
            <CoaItem
              label="New Address"
              class="mt-6"
            >
              {{ newAddress.amsCompleteAddress }}<br>
              {{ newAddress.amsCity }}, {{ newAddress.amsState }} {{ formatZip9(newAddress.amsZip9) }}<br>
              {{ newAddress.amsUrbanizationName }}
            </CoaItem>
          </div>
        </div>
        <router-link
          id="verify-info-edit"
          :to="{ path: `edit${editPagePathParam()}` }"
          class="c-btn c-btn--secondary mt-6 mb-6"
          @click.native="analytics.click.edit"
        >
          Edit
        </router-link>
        <div
          v-if="hasMoreThanOneAddress"
          class="c-hairline"
        />
        <AddressList
          v-if="enhancedBusinessFlowEnabled && coa.moverType === 'BUSINESS' && hasMoreThanOneAddress"
          ref="verify-info-addresses"
          :is-verify-info="true"
          :is-simplify="!!showSimplifyExperience"
          :business="business"
          :bus-address="busAddresses.addresses||[]"
          :selected="orderId"
          @address-clicked="(orderId)=>populateBusiness(orderId)"
        />
      </div>
      <div
        class="additional__service--selections--main--side"
        :class="{'additional__service--selections--main--side--temp': !showSelections && !isPermanent}"
      >
        <div class="additional__service--selections--side--order">
          <table class="choice__experience--shopping-cart">
            <caption>Order Summary</caption>
            <thead>
              <tr>
                <th>Item</th>
                <th>&nbsp;</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="choice in choiceSelections"
                :key="choice.id || choice.optionId"
              >
                <td> {{ choice.title }} </td>
                <td
                  class="choice__selection--row"
                  :class="{'choice__selection--row--selectable': !isRequired(choice)}"
                >
                  <img
                    v-if="!isRequired(choice)"
                    :id="`order-summary-close-icon-${choice.id}`"
                    loading="lazy"
                    style="margin: auto;"
                    class="choice__selection--icon"
                    :src="require('../../assets/choice/icon_choice_close.svg')"
                    alt=""
                    data-alternative-type="checkbox"
                    :data-alternative-value="choice.id"
                    :data-text="choice.id > 0 ? 'Mail Forwarding Extension' : 'Informed Delivery'"
                    data-event-name="ElementClicked"
                    data-outcome="TOGGLE_OFF"
                    data-page-name="VERIFY_INFO"
                    @click="commitSelection($event)"
                  >
                  <small
                    v-if="!isRequired(choice)"
                    :id="`order-summary-remove-${choice.id}`"
                    style="padding: 0 2px;"
                    :class="{'choice__selection--remove': !isRequired(choice)}"
                    data-alternative-type="checkbox"
                    :data-alternative-value="choice.id"
                    :data-text="choice.id > 0 ? 'Mail Forwarding Extension' : 'Informed Delivery'"
                    data-event-name="ElementClicked"
                    data-outcome="TOGGLE_OFF"
                    data-page-name="VERIFY_INFO"
                    @click="commitSelection($event)"
                  >
                    Remove
                  </small>
                </td>
                <td> {{ asCurrency(parseCost(choice)) }} </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total due on next step</td>
                <td />
                <td> {{ asCurrency(choiceSelectionsTotal) }} </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div
          :class="{
            'verify__info--autofill': noSelections,
            'additional__service--selections--side': !noSelections,
          }"
        >
          <section
            :class="{
              'additional-services-selections-hidden': !showSelections
            }"
            class="additional-services-selections"
          >
            <template>
              <h3 class="additional-services-selections-title">
                Your additional {{ pluralize('selection', additionalSelections) }}
              </h3>
              <header class="additional-services-selections-header">
                Item
              </header>
              <article
                v-if="isSelected('vr')"
                key="vr"
                class="additional-services-selections-item"
              >
                <span
                  class="additional-services-selections-item-name"
                  :class="{ 'ineligible-voter-reg': !isPermanent }"
                >
                  Update my voter registration
                </span>
                <span>&nbsp;</span>
                <button
                  v-if="isPermanent"
                  data-choiceselection="vr"
                  class="additional-services-selections-remove"
                  data-mercurial-name="autofillChoice"
                  data-text="Voter Registration"
                  data-event-name="ButtonClicked"
                  data-outcome="TOGGLE_OFF"
                  data-page-name="VERIFY_INFO"
                  type="button"
                  @click="removeSelection($event,'vr')"
                >
                  <img
                    class="additional-services-selections-choice-icon"
                    loading="lazy"
                    alt="blue circle with a while letter X."
                    :src="require('../../assets/choice/icon_choice_close.svg')"
                  >
                  <small v-if="isPermanent" />
                  <img
                    v-if="isPermanent"
                    class="additional-services-selections-choice-icon"
                    :src="require('../../assets/choice/icon_circle_checkmark.svg')"
                    alt="blue checkmark set inside a thin blue circle."
                    loading="lazy"
                  >
                </button>
              </article>
              <article
                v-if="isSelected('save')"
                class="additional-services-selections-item"
              >
                <span class="additional-services-selections-item-name"> Exclusive offers from MYMOVE </span>
                <span>&nbsp;</span>
                <button
                  data-choiceselection="save"
                  class="additional-services-selections-remove"
                  data-mercurial-name="offersChoice"
                  data-text="Exclusive Offers"
                  data-event-name="ButtonClicked"
                  data-outcome="TOGGLE_OFF"
                  data-page-name="VERIFY_INFO"
                  type="button"
                  @click="removeSelection($event,'save')"
                >
                  <img
                    class="additional-services-selections-choice-icon"
                    loading="lazy"
                    alt="blue circle with a while letter X."
                    :src="require('../../assets/choice/icon_choice_close.svg')"
                  >
                  <small />
                  <img
                    class="additional-services-selections-choice-icon"
                    :src="require('../../assets/choice/icon_circle_checkmark.svg')"
                    alt="blue checkmark set inside a thin blue circle."
                    loading="lazy"
                  >
                </button>
              </article>
            </template>
            <info-panel
              id="temporary-mover-no-vr"
              :display-panel="!isPermanent"
              has-note
              note-bold
              info-type="info"
              :info-message="staticText.tempMoverNoVR"
            />
          </section>
          <div
            v-if="showSelections"
            class="c-hairline"
          />
          <p
            v-if="showSelections"
            class="additional__service--registration"
          >
            Want to save time? Pre-fill all your info.
          </p>
          <p
            v-if="!showSelections"
            id="choice--selection-verify-info-autofill"
          >
            Create a free account with MYMOVE to access brand-name
            deals, moving tools, tips and start your voter registration.
          </p>
          <div
            v-if="showSelections"
            class="choice__selection--input"
            :class="{'choice__selection--input--selected': myMovePrefillOptIn}"
          >
            <label class="choice__selection--label">
              Yes, pre-fill my forms
              <input
                ref="verify-info-autofill-vr"
                v-model="myMovePrefillOptIn"
                name="myMovePrefillOptIn"
                data-mercurial-name="myMovePrefillOptIn"
                data-text="Prefill Opt In"
                data-event-name="ElementClicked"
                data-outcome="PREFILL_OPT_IN"
                data-page-name="VERIFY_INFO"
                class="mt-3"
                type="checkbox"
                @input="handleChoice($event)"
              >
              <span class="checkmark" />
            </label>
          </div>
          <p
            class="additional__service--consent"
            :class="{
              'choice__services--consent': !showSelections
            }"
          >
            <label
              class="choice__selection--label"
              :class="{'choice__services--consent--inactive': showSelections}"
            >
              <input
                ref="verify-info-autofill-vr-opt-in"
                v-model="myMoveChoicePrefillOptIn"
                class="mt-3"
                name="myMoveChoicePrefillOptIn"
                data-mercurial-name="myMoveChoicePrefillOptIn"
                data-text="Prefill Opt In Choice Selections Removed"
                data-event-name="ElementClicked"
                data-outcome="PREFILL_OPT_IN"
                data-page-name="VERIFY_INFO"
                type="checkbox"
                @input="handleChoice($event)"
              >
              <span class="checkmark" />
            </label>
            By checking this box, you are voluntarily providing your
            consent to transfer a copy of your personal contact information
            to MYMOVE in order to pre-fill your MYMOVE registration.
          </p>
        </div>
        <div class="additional__service--selections--side--order--note">
          <p>
            This payment will be processed with the {{ asCurrency(choiceSelections[0].cost) }} identity verification fee.
          </p>
          <p
            v-if="extensionSelected"
            class="mt-5"
          >
            This is a non-refundable product. Cancelling your
            Change-of-Address will not refund your Extended
            Mail Service Fee.
          </p>
          <p
            v-if="coa.moverType === 'BUSINESS' && extensionSelected"
            ref="emfIcoaAlias"
            class="mt-5"
          >
            Note: The Extended Mail Forwarding only applies to the primary business name.
            If you want Extended Mail Forwarding for Business Alias names, you must access
            the Official USPS Change-of-Address Correct Form at
            <a
              class="c-link--inline weight-normal"
              rel="noopener noreferrer"
              :href="this.$sanitize(coacUrl)"
              target="new"
            >{{ coacUrl }}</a>
          </p>
        </div>
      </div>
    </div>
    <!-- Choice verifyInfo old design---------------------------------------------------------------------------------------------------- -->
    <div v-else-if="showChoiceExperience || showSimplifyExperience">
      <section
        ref="infoRead"
        class="py-8"
      >
        <h3 class="font-bold">
          Move Information
        </h3>
        <!-- Display if the modalNewConstruction is opened and they hit "Yes" or "No" -->
        <template
          v-if="overrideAms"
        >
          <ErrorAlert
            v-if="$route.query.errorException === 'NewAddressFieldFullAddressException'"
            ref="newAddressFieldFullAddress"
            class="mt-2"
          >
            We cannot find the address in our records. The "Street Address" field should not include the city, state or ZIP. Please click the "Edit" button to remove the city, state or ZIP, then click "Save." If you are sure this is the correct address, you may proceed by clicking "Next."
          </ErrorAlert>
          <ErrorAlert
            v-if="$route.query.errorException === 'InvalidPrimaryInformationException'"
            ref="invalidPrimaryInformation"
            class="mt-2"
          >
            We cannot locate the entered street number in our records. Please click the "Edit" button to correct the street number of the street address, then click "Save." If you are certain the existing address is correct, you may proceed by clicking "Next."
          </ErrorAlert>
          <ErrorAlert
            v-if="$route.query.errorException === 'UnneededSecondaryInformationException'"
            ref="unneededSecondaryInformation"
            class="mt-2"
          >
            According to our records, there is no unit or apartment number associated with this address. Please verify the address for accuracy and click "Edit" then "Save" to make corrections.
          </ErrorAlert>
          <ErrorAlert
            v-if="$route.query.errorException === 'InvalidSecondaryInformationException'"
            ref="invalidSecondaryInformation"
            class="mt-2"
          >
            A unit or apartment number is either missing, incomplete or invalid. Please click "Edit" to add the correct number/designation after the street name in the "Street" field, then click "Save." USPS will standardize your address upon submission.
          </ErrorAlert>
          <ErrorAlert
            v-if="$route.query.errorException === 'AmsInvalidAddressException'"
            ref="invalid"
            class="mt-2"
          >
            We cannot find the address you're moving to in our records. Please verify the address for accuracy and click "Edit" then "Save" to make corrections.
          </ErrorAlert>
          <!-- default error when multiple businesses -->
          <ErrorAlert
            v-if="isMultipleAddressCoa"
            ref="invalid"
            class="mt-2"
          >
            We cannot find the address you're moving to in our records. Please verify the address for accuracy and click "Edit" then "Update" to make corrections. If you are certain the existing address is correct, you may proceed by clicking "Next."
          </ErrorAlert>
          <ErrorAlert
            ref="overrideMessage"
            class="mt-2"
          >
            Errors with your address may result in delays with mail forwarding.
          </ErrorAlert>
        </template>
        <div class="flex mt-6">
          <div class="w-1/2">
            <CoaItem
              v-if="coa.moverType === 'BUSINESS'"
              id="business-name"
              label="Business Name"
            >
              {{ coa.businessName }}

              <p :class="{'verify-info-alias-list' : businessAliases.length >= 5}">
                <span
                  v-for="alias in businessAliases"
                  :key="alias"
                >
                  <br>{{ alias }}
                </span>
              </p>
            </CoaItem>
            <CoaItem
              v-else
              label="Name"
            >
              {{ [mover.firstName, mover.middleName, mover.lastName ] | formatName }}
            </CoaItem>
            <CoaItem
              label="Who's Moving?"
              class="mt-6"
            >
              {{ coa.moverType }}
            </CoaItem>
            <CoaItem
              label="Email Address"
              class="mt-6"
            >
              {{ mover.email }}
            </CoaItem>
            <CoaItem
              label="Phone Number"
              class="mt-6"
            >
              {{ (mover.homePhone || mover.mobilePhone) | formatPhone }}
            </CoaItem>
            <CoaItem
              label="Phone Type"
              class="mt-6"
            >
              {{ mover.homePhone && 'Home' }}
              {{ mover.mobilePhone && 'Mobile' }}
            </CoaItem>
          </div>
          <div class="w-1/2">
            <CoaItem label="Type of Move">
              {{ coa.forwardType }}
            </CoaItem>
            <CoaItem
              v-if="coa.forwardType === 'PERMANENT'"
              label="Requested Mail Forwarding Date"
              class="mt-6"
            >
              <!-- Monday, September 14, 2020 -->
              {{ parseAndFormatDate(coa.startDate) }}
            </CoaItem>
            <template v-if="coa.forwardType === 'TEMPORARY'">
              <CoaItem
                label="Requested Start Forwarding Date"
                class="mt-6"
              >
                {{ parseAndFormatDate(coa.startDate) }}
              </CoaItem>
              <CoaItem
                label="Requested End Forwarding Date"
                class="mt-6"
              >
                {{ parseAndFormatDate(coa.stopDate) }}
              </CoaItem>
            </template>
            <CoaItem
              label="Old Address"
              class="mt-6"
            >
              {{ oldAddress.amsCompleteAddress }}<br>
              {{ oldAddress.amsCity }}, {{ oldAddress.amsState }} {{ formatZip9(oldAddress.amsZip9) }}<br>
              {{ oldAddress.amsUrbanizationName }}
            </CoaItem>
            <CoaItem
              label="New Address"
              class="mt-6"
            >
              {{ newAddress.amsCompleteAddress }}<br>
              {{ newAddress.amsCity }}, {{ newAddress.amsState }} {{ formatZip9(newAddress.amsZip9) }}<br>
              {{ newAddress.amsUrbanizationName }}
            </CoaItem>
          </div>
        </div>
        <router-link
          id="verify-info-edit"
          :to="{ path: `edit${editPagePathParam()}` }"
          class="c-btn c-btn--secondary mt-6 mb-6"
          @click.native="analytics.click.edit"
        >
          Edit
        </router-link>
        <div
          v-if="hasMoreThanOneAddress"
          class="c-hairline"
        />
        <AddressList
          v-if="enhancedBusinessFlowEnabled && coa.moverType === 'BUSINESS' && hasMoreThanOneAddress"
          ref="verify-info-addresses"
          :is-verify-info="true"
          :is-simplify="!!showSimplifyExperience"
          :business="business"
          :bus-address="busAddresses.addresses||[]"
          :selected="orderId"
          @address-clicked="(orderId)=>populateBusiness(orderId)"
        />
      </section>
      <div class="c-hairline" />
      <div
        id="experience"
        ref="experience"
        class="mt-8"
      />
      <div
        class="choice__experience--services"
      >
        <div class="choice__experience--services--grid-item">
          <table class="choice__experience--shopping-cart">
            <caption>Order Summary</caption>
            <thead>
              <tr>
                <th>Item</th>
                <th>&nbsp;</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="choice in choiceSelections"
                :key="choice.id || choice.optionId"
              >
                <td> {{ choice.title }} </td>
                <td
                  class="choice__selection--row"
                  :class="{'choice__selection--row--selectable': !isRequired(choice)}"
                >
                  <img
                    v-if="!isRequired(choice)"
                    :id="`order-summary-close-icon-${choice.id}`"
                    loading="lazy"
                    style="margin: auto;"
                    class="choice__selection--icon"
                    :src="require('../../assets/choice/icon_choice_close.svg')"
                    alt=""
                    data-alternative-type="checkbox"
                    :data-alternative-value="choice.id"
                    :data-text="choice.id > 0 ? 'Mail Forwarding Extension' : 'Informed Delivery'"
                    data-event-name="ElementClicked"
                    data-outcome="TOGGLE_OFF"
                    data-page-name="VERIFY_INFO"
                    @click="commitSelection($event)"
                  >
                  <small
                    v-if="!isRequired(choice)"
                    :id="`order-summary-remove-${choice.id}`"
                    style="padding: 0 2px;"
                    :class="{'choice__selection--remove': !isRequired(choice)}"
                    data-alternative-type="checkbox"
                    :data-alternative-value="choice.id"
                    :data-text="choice.id > 0 ? 'Mail Forwarding Extension' : 'Informed Delivery'"
                    data-event-name="ElementClicked"
                    data-outcome="TOGGLE_OFF"
                    data-page-name="VERIFY_INFO"
                    @click="commitSelection($event)"
                  >
                    Remove
                  </small>
                </td>
                <td> {{ asCurrency(parseCost(choice)) }} </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total due on next step</td>
                <td />
                <td> {{ asCurrency(choiceSelectionsTotal) }} </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div class="choice__experience--services--grid-item">
          <p>
            This payment will be processed with the {{ asCurrency(choiceSelections[0].cost) }} identity verification fee.
          </p>
          <p
            v-if="extensionSelected"
            class="mt-5"
          >
            This is a non-refundable product. Cancelling your
            Change-of-Address will not refund your Extended
            Mail Service Fee.
          </p>
          <p
            v-if="coa.moverType === 'BUSINESS' && extensionSelected"
            ref="emfIcoaAlias"
            class="mt-5"
          >
            Note: The Extended Mail Forwarding only applies to the primary business name.
            If you want Extended Mail Forwarding for Business Alias names, you must access
            the Official USPS Change-of-Address Correct Form at
            <a
              class="c-link--inline weight-normal"
              rel="noopener noreferrer"
              :href="this.$sanitize(coacUrl)"
              target="new"
            >{{ coacUrl }}</a>
          </p>
        </div>
        <div
          class="choice__experience--services--grid-item"
          :class="{
            'verify__info--autofill': noSelections,
            'additional__service--selections': !noSelections,
          }"
        >
          <section
            :class="{
              'additional-services-selections-hidden': !showSelections
            }"
            class="additional-services-selections"
          >
            <template>
              <h3 class="additional-services-selections-title">
                Your additional {{ pluralize('selection', additionalSelections) }}
              </h3>
              <header class="additional-services-selections-header">
                Item
              </header>
              <article
                v-if="isSelected('vr')"
                key="vr"
                class="additional-services-selections-item"
              >
                <span
                  class="additional-services-selections-item-name"
                  :class="{ 'ineligible-voter-reg': !isPermanent }"
                >
                  Update my voter registration
                </span>
                <span>&nbsp;</span>
                <button
                  v-if="isPermanent"
                  data-choiceselection="vr"
                  class="additional-services-selections-remove"
                  data-mercurial-name="autofillChoice"
                  data-text="Voter Registration"
                  data-event-name="ButtonClicked"
                  data-outcome="TOGGLE_OFF"
                  data-page-name="VERIFY_INFO"
                  type="button"
                  @click="removeSelection($event,'vr')"
                >
                  <img
                    class="additional-services-selections-choice-icon"
                    loading="lazy"
                    alt="blue circle with a while letter X."
                    :src="require('../../assets/choice/icon_choice_close.svg')"
                  >
                  <small v-if="isPermanent" />
                  <img
                    v-if="isPermanent"
                    class="additional-services-selections-choice-icon"
                    :src="require('../../assets/choice/icon_circle_checkmark.svg')"
                    alt="blue checkmark set inside a thin blue circle."
                    loading="lazy"
                  >
                </button>
              </article>
              <article
                v-if="isSelected('save')"
                class="additional-services-selections-item"
              >
                <span class="additional-services-selections-item-name"> Exclusive offers from MYMOVE </span>
                <span>&nbsp;</span>
                <button
                  data-choiceselection="save"
                  class="additional-services-selections-remove"
                  data-mercurial-name="offersChoice"
                  data-text="Exclusive Offers"
                  data-event-name="ButtonClicked"
                  data-outcome="TOGGLE_OFF"
                  data-page-name="VERIFY_INFO"
                  type="button"
                  @click="removeSelection($event,'save')"
                >
                  <img
                    class="additional-services-selections-choice-icon"
                    loading="lazy"
                    alt="blue circle with a while letter X."
                    :src="require('../../assets/choice/icon_choice_close.svg')"
                  >
                  <small />
                  <img
                    class="additional-services-selections-choice-icon"
                    :src="require('../../assets/choice/icon_circle_checkmark.svg')"
                    alt="blue checkmark set inside a thin blue circle."
                    loading="lazy"
                  >
                </button>
              </article>
            </template>
            <info-panel
              id="temporary-mover-no-vr"
              :display-panel="!isPermanent"
              has-note
              note-bold
              info-type="info"
              :info-message="staticText.tempMoverNoVR"
            />
          </section>
          <div
            v-if="showSelections"
            class="c-hairline"
          />
          <p
            v-if="showSelections"
            class="additional__service--registration"
          >
            Want to save time? Pre-fill all your info.
          </p>
          <p
            v-if="!showSelections"
            id="choice--selection-verify-info-autofill"
          >
            Create a free account with MYMOVE to access brand-name
            deals, moving tools, tips and start your voter registration.
          </p>
          <div
            v-if="showSelections"
            class="choice__selection--input"
            :class="{'choice__selection--input--selected': myMovePrefillOptIn}"
          >
            <label class="choice__selection--label">
              Yes, pre-fill my forms
              <input
                ref="verify-info-autofill-vr"
                v-model="myMovePrefillOptIn"
                name="myMovePrefillOptIn"
                data-mercurial-name="myMovePrefillOptIn"
                data-text="Prefill Opt In"
                data-event-name="ElementClicked"
                data-outcome="PREFILL_OPT_IN"
                data-page-name="VERIFY_INFO"
                class="mt-3"
                type="checkbox"
                @input="handleChoice($event)"
              >
              <span class="checkmark" />
            </label>
          </div>
          <p
            class="additional__service--consent"
            :class="{
              'choice__services--consent': !showSelections
            }"
          >
            <label
              class="choice__selection--label"
              :class="{'choice__services--consent--inactive': showSelections}"
            >
              <input
                ref="verify-info-autofill-vr-opt-in"
                v-model="myMoveChoicePrefillOptIn"
                class="mt-3"
                name="myMoveChoicePrefillOptIn"
                data-mercurial-name="myMoveChoicePrefillOptIn"
                data-text="Prefill Opt In Choice Selections Removed"
                data-event-name="ElementClicked"
                data-outcome="PREFILL_OPT_IN"
                data-page-name="VERIFY_INFO"
                type="checkbox"
                @input="handleChoice($event)"
              >
              <span class="checkmark" />
            </label>
            By checking this box, you are voluntarily providing your
            consent to transfer a copy of your personal contact information
            to MYMOVE in order to pre-fill your MYMOVE registration.
          </p>
        </div>
      </div>
    </div>
    <!-- Control verifyInfo -------------------------------------------------------------------------------------------------------- -->
    <section
      v-else
      ref="infoRead"
      class="py-8"
    >
      <h3 class="font-bold">
        Move Information
      </h3>
      <!-- Display if the modalNewConstruction is opened and they hit "Yes" or "No" -->
      <template
        v-if="overrideAms"
      >
        <ErrorAlert
          v-if="$route.query.errorException === 'NewAddressFieldFullAddressException'"
          ref="newAddressFieldFullAddress"
          class="mt-2"
        >
          We cannot find the address in our records. The "Street Address" field should not include the city, state or ZIP. Please click the "Edit" button to remove the city, state or ZIP, then click "Save." If you are sure this is the correct address, you may proceed by clicking "Next."
        </ErrorAlert>
        <ErrorAlert
          v-if="$route.query.errorException === 'InvalidPrimaryInformationException'"
          ref="invalidPrimaryInformation"
          class="mt-2"
        >
          We cannot locate the entered street number in our records. Please click the "Edit" button to correct the street number of the street address, then click "Save." If you are certain the existing address is correct, you may proceed by clicking "Next."
        </ErrorAlert>
        <ErrorAlert
          v-if="$route.query.errorException === 'UnneededSecondaryInformationException'"
          ref="unneededSecondaryInformation"
          class="mt-2"
        >
          According to our records, there is no unit or apartment number associated with this address. Please verify the address for accuracy and click "Edit" then "Save" to make corrections.
        </ErrorAlert>
        <ErrorAlert
          v-if="$route.query.errorException === 'InvalidSecondaryInformationException'"
          ref="invalidSecondaryInformation"
          class="mt-2"
        >
          A unit or apartment number is either missing, incomplete or invalid. Please click "Edit" to add the correct number/designation after the street name in the "Street" field, then click "Save." USPS will standardize your address upon submission.
        </ErrorAlert>
        <ErrorAlert
          v-if="$route.query.errorException === 'AmsInvalidAddressException'"
          ref="invalid"
          class="mt-2"
        >
          We cannot find the address you're moving to in our records. Please verify the address for accuracy and click "Edit" then "Save" to make corrections.
        </ErrorAlert>
        <!-- default error when multiple businesses -->
        <ErrorAlert
          v-if="isMultipleAddressCoa"
          ref="invalid"
          class="mt-2"
        >
          We cannot find the address you're moving to in our records. Please verify the address for accuracy and click "Edit" then "Update" to make corrections. If you are certain the existing address is correct, you may proceed by clicking "Next."
        </ErrorAlert>
        <ErrorAlert
          ref="overrideMessage"
          class="mt-2"
        >
          Errors with your address may result in delays with mail forwarding.
        </ErrorAlert>
      </template>
      <div class="flex mt-6">
        <div class="w-1/2">
          <CoaItem
            v-if="coa.moverType === 'BUSINESS'"
            id="business-name"
            label="Business Name"
          >
            {{ coa.businessName }}

            <p :class="{'verify-info-alias-list' : businessAliases.length >= 5}">
              <span
                v-for="alias in businessAliases"
                :key="alias"
              >
                <br>{{ alias }}
              </span>
            </p>
          </CoaItem>
          <CoaItem
            v-else
            label="Name"
          >
            {{ [mover.firstName, mover.middleName, mover.lastName ] | formatName }}
          </CoaItem>
          <CoaItem
            label="Who's Moving?"
            class="mt-6"
          >
            {{ coa.moverType }}
          </CoaItem>
          <CoaItem
            label="Email Address"
            class="mt-6"
          >
            {{ mover.email }}
          </CoaItem>
          <CoaItem
            label="Phone Number"
            class="mt-6"
          >
            {{ (mover.homePhone || mover.mobilePhone) | formatPhone }}
          </CoaItem>
          <CoaItem
            label="Phone Type"
            class="mt-6"
          >
            {{ mover.homePhone && 'Home' }}
            {{ mover.mobilePhone && 'Mobile' }}
          </CoaItem>
        </div>
        <div class="w-1/2">
          <CoaItem label="Type of Move">
            {{ coa.forwardType }}
          </CoaItem>
          <CoaItem
            v-if="coa.forwardType === 'PERMANENT'"
            label="Requested Mail Forwarding Date"
            class="mt-6"
          >
            <!-- Monday, September 14, 2020 -->
            {{ parseAndFormatDate(coa.startDate) }}
          </CoaItem>
          <template v-if="coa.forwardType === 'TEMPORARY'">
            <CoaItem
              label="Requested Start Forwarding Date"
              class="mt-6"
            >
              {{ parseAndFormatDate(coa.startDate) }}
            </CoaItem>
            <CoaItem
              label="Requested End Forwarding Date"
              class="mt-6"
            >
              {{ parseAndFormatDate(coa.stopDate) }}
            </CoaItem>
          </template>
          <CoaItem
            label="Old Address"
            class="mt-6"
          >
            {{ oldAddress.amsCompleteAddress }}<br>
            {{ oldAddress.amsCity }}, {{ oldAddress.amsState }} {{ formatZip9(oldAddress.amsZip9) }}<br>
            {{ oldAddress.amsUrbanizationName }}
          </CoaItem>
          <CoaItem
            label="New Address"
            class="mt-6"
          >
            {{ newAddress.amsCompleteAddress }}<br>
            {{ newAddress.amsCity }}, {{ newAddress.amsState }} {{ formatZip9(newAddress.amsZip9) }}<br>
            {{ newAddress.amsUrbanizationName }}
          </CoaItem>
        </div>
      </div>
      <router-link
        id="verify-info-edit"
        :to="{ path: `edit${editPagePathParam()}` }"
        class="c-btn c-btn--secondary mt-6 mb-6"
        @click.native="analytics.click.edit"
      >
        Edit
      </router-link>
      <div
        v-if="hasMoreThanOneAddress"
        class="c-hairline"
      />
      <AddressList
        v-if="enhancedBusinessFlowEnabled && coa.moverType === 'BUSINESS' && hasMoreThanOneAddress"
        ref="verify-info-addresses"
        :is-verify-info="true"
        :is-simplify="!!showSimplifyExperience"
        :business="business"
        :bus-address="busAddresses.addresses||[]"
        :selected="orderId"
        @address-clicked="(orderId)=>populateBusiness(orderId)"
      />
    </section>
    <div
      v-if="!showSelections"
      class="c-hairline"
    />
    <div class="flex items-center mt-6">
      <router-link
        id="back"
        ref="back"
        :to="{ name: handleBack, query: $route.query }"
        class="c-btn c-btn--secondary mr-6"
        @click.native="analytics.click.back"
      >
        Back
      </router-link>
      <button
        id="next"
        ref="nextBtn"
        class="c-btn"
        @click="next(); analytics.click.next()"
      >
        Next
      </button>
    </div>

    <Modal
      ref="coaCheckModal"
      v-model="coaCheckModal"
    >
      <h4 class="font-bold">
        Based on your response...
      </h4>
      <p class="mt-3">
        A Change-of-Address already exists matching the name and address
        you've entered. To make changes to your current Change-of-Address,
        visit
        <a
          :class="['c-link--choice', 'c-link--inline']"
          :href="this.$sanitize($options.coacUrl)"
          rel="noopener noreferrer"
        >
          {{ $options.coacUrl }}
        </a>, enter your new ZIP Code™ and 16-digit confirmation code,
        which can be located within your confirmation email and the
        Confirmation Notification Letter you received at your new address.
      </p>
    </Modal>
  </div>
</template>

<script>
import {
  Banner, CoaItem, ErrorAlert, Modal,
} from '@mgo/ui/lib/components';
import InfoPanel from '../../components/info-panel.vue';
import { desktopAnalytics } from './analytics';
import verifyInfoMixin from './mixin';
import AddressList from '../../components/MultipleAddresses/AddressList.vue';

export default {
  name: 'DesktopVerifyInfoRead',
  components: {
    ErrorAlert,
    Banner,
    CoaItem,
    InfoPanel,
    AddressList,
    Modal,
  },
  mixins: [verifyInfoMixin],
  computed: {
    showVerifyInfoWideView: () => JSON.parse(sessionStorage.getItem('showVerifyInfoWideView')),
    analytics() {
      return desktopAnalytics();
    },
    handleBack() {
      sessionStorage.setItem('myMovePrefillOptIn', this.myMovePrefillOptIn);
      sessionStorage.setItem('myMoveChoicePrefillOptIn', this.myMoveChoicePrefillOptIn);
      sessionStorage.setItem('informedDeliveryEligible', this.informedDeliveryEligible);
      sessionStorage.setItem('informedDelivery', this.informedDelivery);

      if ((this.isMultipleAddressCoa && this.isMultiBuzPrimaryPermanent)
      || (!this.isMultipleAddressCoa && this.isPermanent)) {
        return 'additional-services';
      }
      if (this.showChoiceExperience || this.showNonControlExperience) {
        return 'move-combined';
      }
      return 'contact-info';
    },
  },
  async mounted() {
    // enable loader for redirect
    this.$store.commit('ENABLE_LOADER');

    // await the first request
    // so we don't request multiple auth tokens
    await this.hydrateCOA();
    /*
     * redirect back to disclaimer
     * if base data (startDate) isn't available
     * or coa complete is true
     */
    this.oldAddressError = '';
    this.newAddressError = '';
    if (!this.coa.startDate || this.coa.complete) {
      this.$router.push({ name: 'disclaimer' });
      this.$store.commit('DISABLE_LOADER');
      return;
    }

    this.hydrateMover();

    if (!this.coa.multipleAddresses) {
      this.hydrateAddresses();
    }

    if (this.flagAmpersendEnabled) {
      await this.$amp.getUnimpressedExperiences();
    }
    if (this.moverType === 'BUSINESS') {
      this.choiceSelections = 0;
    }
    this.$store.commit('DISABLE_LOADER');
  },
};
</script>
<style scoped>
@import '../../css/components/button-card.css';
@import '../../css/components/additional-services-cart.css';
@import '../../css/components/choice-verify-info.css';
</style>
